import { useEffect, useRef, useState } from 'react';
import { voiceCaptureActive } from './consoleVoice';
import type { Editor } from '@tiptap/react';

interface SpeechResult {
  isFinal: boolean;
  0: { transcript: string };
}
export interface HuddleRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<SpeechResult> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  onspeechstart: (() => void) | null;
  onspeechend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechWindow = Window & {
  SpeechRecognition?: new () => HuddleRecognition;
  webkitSpeechRecognition?: new () => HuddleRecognition;
};

export function useHuddleDictation(editor: Editor | null, disabled: boolean) {
  const Recognition =
    (window as SpeechWindow).SpeechRecognition ?? (window as SpeechWindow).webkitSpeechRecognition;
  const [voiceCapturing, setVoiceCapturing] = useState(voiceCaptureActive);
  const voiceCaptureRef = useRef(voiceCaptureActive);
  useEffect(() => {
    const onCapture = (event: Event) => {
      const active = Boolean((event as CustomEvent<boolean>).detail);
      voiceCaptureRef.current = active;
      setVoiceCapturing(active);
      if (active) cancel.current();
    };
    window.addEventListener('console-voice-capture', onCapture);
    return () => window.removeEventListener('console-voice-capture', onCapture);
  }, []);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [blocked, setBlocked] = useState(false);
  const session = useRef<HuddleRecognition | null>(null);
  const stop = useRef<() => void>(() => {});
  const cancel = useRef<() => void>(() => {});

  useEffect(() => {
    // Send disables the editor; a closed card unmounts it. Neither may accept
    // late results into a draft that has already been sent or hidden.
    if (disabled) cancel.current();
    return () => cancel.current();
  }, [disabled, editor]);

  function toggle() {
    if (session.current !== null) {
      stop.current();
      return;
    }
    if (!Recognition || !editor || disabled || voiceCaptureRef.current || voiceCaptureActive)
      return;
    const recognition = new Recognition();
    session.current = recognition;
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopFallback: ReturnType<typeof setTimeout> | undefined;
    let speaking = false;
    let stopping = false;
    const finalised = new Set<number>();
    const clearSilence = () => clearTimeout(timer);
    const armSilence = () => {
      clearSilence();
      timer = setTimeout(() => {
        stop.current();
      }, 60_000);
    };
    const finish = () => {
      if (session.current !== recognition) return;
      clearSilence();
      clearTimeout(stopFallback);
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.onspeechstart = null;
      recognition.onspeechend = null;
      session.current = null;
      stop.current = () => {};
      cancel.current = () => {};
      setListening(false);
      setInterim('');
    };
    cancel.current = () => {
      finish();
      recognition.abort();
    };
    stop.current = () => {
      if (stopping || session.current !== recognition) return;
      stopping = true;
      setListening(false);
      recognition.stop();
      if (session.current === recognition) {
        stopFallback = setTimeout(() => cancel.current(), 1_500);
      }
    };
    recognition.onresult = ({ results }) => {
      if (session.current !== recognition || editor.isDestroyed || !editor.isEditable) return;
      if (!speaking) armSilence();
      let preview = '';
      for (let index = 0; index < results.length; index++) {
        const result = results[index];
        if (!result) continue;
        if (!result.isFinal) {
          preview += result[0].transcript;
        } else if (!finalised.has(index)) {
          finalised.add(index);
          const text = result[0].transcript;
          if (text) {
            // Text nodes, never HTML: spoken markup must stay literal. Tiptap
            // retains the caret while the toolbar is clicked, and maps it as
            // the user edits. Interim words are only a preview inside the box.
            editor.commands.insertContent({ type: 'text', text });
          }
        }
      }
      setInterim(preview);
    };
    recognition.onerror = ({ error }) => {
      if (error === 'not-allowed' || error === 'service-not-allowed') setBlocked(true);
      cancel.current();
    };
    recognition.onend = finish;
    recognition.onspeechstart = () => {
      speaking = true;
      clearSilence();
    };
    recognition.onspeechend = () => {
      speaking = false;
      armSilence();
    };
    setBlocked(false);
    setListening(true);
    armSilence();
    try {
      recognition.start();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'NotAllowedError') setBlocked(true);
      cancel.current();
    }
  }

  return {
    voiceCapturing,
    supported: Boolean(Recognition),
    listening,
    interim,
    blocked,
    toggle,
    cancel: () => cancel.current(),
  };
}
